import { Box, ButtonBase, IconButton, Theme, Tooltip, Typography } from "@mui/material";
import { ArrowDownwardRounded, ArrowUpwardRounded, DeleteOutlineRounded, VisibilityOutlined, WhatsApp } from "@mui/icons-material";
import { DataTableColumn, PatientHexAvatar, TagChip } from "../../../components/ui";
import { Patient } from "../../../services/patient.service";
import { PatientSortOrder } from "./patients-filters";
import { formatPatientDate, lastConsultationDate, phoneMask, whatsappUrl } from "./patient-format";

type Args = {
  theme: Theme;
  sortOrder: PatientSortOrder;
  onView: (id: number) => void;
  onDelete: (id: number) => void;
};

const headerWithSort = (label: string, active: boolean, direction: "asc" | "desc") => (
  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
    <span>{label}</span>
    {active && (direction === "asc" ? <ArrowUpwardRounded sx={{ fontSize: 13, color: "primary.main" }} /> : <ArrowDownwardRounded sx={{ fontSize: 13, color: "primary.main" }} />)}
  </Box>
);

export function buildPatientColumns({ theme, sortOrder, onView, onDelete }: Args): DataTableColumn<Patient>[] {
  const actionBtn = {
    width: 34,
    height: 34,
    borderRadius: "9px",
    border: `1.5px solid ${theme.palette.divider}`,
  } as const;

  return [
    {
      key: "name",
      header: headerWithSort("Paciente", sortOrder === "name-asc" || sortOrder === "name-desc", sortOrder === "name-desc" ? "desc" : "asc"),
      width: "2.4fr",
      render: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <PatientHexAvatar name={row.name} size={40} />
          <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "text.primary" }}>{row.name}</Typography>
        </Box>
      ),
    },
    {
      key: "phone",
      header: "Telefone",
      width: "1.3fr",
      render: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>{row.phone ? phoneMask.onValue(row.phone) : "—"}</Typography>
          {whatsappUrl(row.phone) && (
            <Tooltip title="Abrir conversa no WhatsApp" placement="top">
              <IconButton
                size="small"
                component="a"
                href={whatsappUrl(row.phone)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Abrir conversa no WhatsApp"
                onClick={(e) => e.stopPropagation()}
                sx={{ color: "#25D366", p: 0.25 }}
              >
                <WhatsApp sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      key: "consultationDate",
      header: headerWithSort(
        "Última consulta",
        sortOrder === "consultation-desc" || sortOrder === "consultation-asc",
        sortOrder === "consultation-desc" ? "desc" : "asc"
      ),
      width: "1.3fr",
      render: (row) => <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>{formatPatientDate(lastConsultationDate(row))}</Typography>,
    },
    {
      key: "returnDate",
      header: headerWithSort("Retorno", sortOrder === "return-desc" || sortOrder === "return-asc", sortOrder === "return-desc" ? "desc" : "asc"),
      width: "1.3fr",
      render: (row) => <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>{formatPatientDate(row.returnDate)}</Typography>,
    },
    {
      key: "tags",
      header: "Tags",
      width: "1.8fr",
      render: (row) => {
        const tags = row.tags ?? [];
        const visible = tags.slice(0, 2);
        const extra = tags.length - 2;
        return (
          <Box sx={{ display: "flex", gap: "5px", flexWrap: "wrap", alignItems: "center" }}>
            {visible.map((tag) => (
              <TagChip key={tag} label={tag} />
            ))}
            {extra > 0 && <Typography sx={{ fontSize: "11px", color: "text.secondary", fontWeight: 600 }}>+{extra}</Typography>}
          </Box>
        );
      },
    },
    {
      key: "actions",
      header: "Ações",
      width: "96px",
      render: (row) => (
        <Box sx={{ display: "flex", gap: "4px" }} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Ver detalhe" placement="top">
            <ButtonBase onClick={() => onView(row.id)} sx={{ ...actionBtn, color: "text.secondary" }} aria-label="Ver detalhe">
              <VisibilityOutlined sx={{ fontSize: 17 }} />
            </ButtonBase>
          </Tooltip>
          <Tooltip title="Deletar paciente" placement="top">
            <ButtonBase onClick={() => onDelete(row.id)} sx={{ ...actionBtn, color: "error.main" }} aria-label="Deletar paciente">
              <DeleteOutlineRounded sx={{ fontSize: 17 }} />
            </ButtonBase>
          </Tooltip>
        </Box>
      ),
    },
  ];
}
